import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney, formatPct, parseAbbrev } from "@/lib/format";
import useMediaQuery from "@/lib/useMediaQuery";
import DecisionBadge from "@/components/DecisionBadge";
import StatusBadge from "@/components/StatusBadge";
import StatusToggle from "@/components/StatusToggle";
import InlineRank from "@/components/InlineRank";
import { SortChips, toggleSort, sortRows } from "@/components/SortChips";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { ChevronDown, ChevronRight, Trash2, Pencil, StickyNote, Save, ExternalLink, RefreshCw as RefreshCwIcon } from "lucide-react";
import usePullToRefresh from "@/lib/usePullToRefresh";

function frDate(dstr) {
  const d = new Date(dstr + "T00:00:00");
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

// Composed gain — iter 26: Test uses (close - open)/open × 100, Real keeps
// (exit or close - entry)/entry × 100. Legacy percent path as fallback.
function composedGain(stock) {
  const isReal = (stock.status || "test").toLowerCase() === "real";
  const openP = stock.open_price ?? stock.criteria?.open_price;
  const ep = stock.entry_price, xp = stock.exit_price, cp = stock.close_price;
  if (!isReal && openP != null && openP > 0) {
    const ref = cp != null ? cp : xp;
    if (ref != null) return (ref / openP - 1) * 100;
    // fall through if no close yet
  }
  if (ep != null && ep > 0) {
    const ref = xp != null ? xp : cp;
    if (ref == null) return null;
    return (ref / ep - 1) * 100;
  }
  const entryPct = stock.entry?.percent, exitPct = stock.exit?.percent, closePct = stock.close?.percent;
  if (entryPct == null) return null;
  const refPct = exitPct != null ? exitPct : closePct;
  if (refPct == null) return null;
  const denom = 1 + entryPct / 100;
  if (denom <= 0) return null;
  return ((1 + refPct / 100) / denom - 1) * 100;
}

// -----------------------------------------------------------------------------
// Inline pencils (desktop table)
// -----------------------------------------------------------------------------
function InlineRankEnd({ stock, onUpdated }) {
  const initial = stock.rank_end != null ? String(stock.rank_end) : "";
  const [v, setV] = useState(initial);
  const [editing, setEditing] = useState(false);
  useEffect(() => setV(stock.rank_end != null ? String(stock.rank_end) : ""), [stock.rank_end]);

  const commit = async () => {
    try {
      const n = v === "" ? null : Math.max(0, parseInt(v, 10) || 0);
      const r = await api.patch(`/stocks/${stock.id}`, { rank_end: n });
      toast.success(`${stock.symbol} — rang de fin ${n === 0 ? "= hors top" : "mis à jour"}`);
      setEditing(false);
      onUpdated?.(r.data);
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    }
  };

  if (!editing) {
    const disp = stock.rank_end;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditing(true); }}
        className="inline-flex items-center gap-1 font-mono-num hover:underline text-app"
        data-testid={`inline-rankend-btn-${stock.symbol}`}
      >
        {disp == null ? "—" : disp === 0 ? "hors top" : `#${disp}`} <Pencil size={10} className="text-muted-2" />
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
      <Input
        autoFocus value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        className="h-7 w-16 bg-input border-line-strong font-mono-num text-right text-xs"
        data-testid={`inline-rankend-input-${stock.symbol}`}
      />
      <Button onClick={commit} className="h-7 px-2 btn-primary text-[10px]" data-testid={`inline-rankend-save-${stock.symbol}`}>OK</Button>
    </div>
  );
}

function InlinePct({ stock, field, label, testKey, onUpdated }) {
  const raw = stock?.[field]?.percent;
  const [v, setV] = useState(raw != null ? String(raw) : "");
  const [editing, setEditing] = useState(false);
  useEffect(() => setV(raw != null ? String(raw) : ""), [raw]);

  const commit = async () => {
    try {
      const pct = v === "" ? null : parseAbbrev(v);
      const priorTs = stock?.[field]?.timestamp || null;
      const body = pct === null
        ? { [field]: { percent: null, timestamp: priorTs } }
        : { [field]: { percent: pct, timestamp: priorTs || new Date().toISOString() } };
      const r = await api.patch(`/stocks/${stock.id}`, body);
      toast.success(`${stock.symbol} — ${label} enregistré`);
      setEditing(false);
      onUpdated?.(r.data);
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    }
  };

  if (!editing) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditing(true); }}
        className={`inline-flex items-center gap-1 font-mono-num hover:underline ${
          raw == null ? "text-muted-2" : raw >= 0 ? "text-invest" : "text-ignore"
        }`}
        data-testid={`inline-${testKey}-btn-${stock.symbol}`}
      >
        {raw == null ? "—" : formatPct(raw)} <Pencil size={10} className="text-muted-2" />
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
      <Input
        autoFocus value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        className="h-7 w-20 bg-input border-line-strong font-mono-num text-right text-xs"
        data-testid={`inline-${testKey}-input-${stock.symbol}`}
      />
      <Button onClick={commit} className="h-7 px-2 btn-primary text-[10px]" data-testid={`inline-${testKey}-save-${stock.symbol}`}>OK</Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Mobile Sheet — edit all fields comfortably on small screens (iter11: prices)
// -----------------------------------------------------------------------------
function StockEditSheet({ open, onOpenChange, stock, onSaved }) {
  const [f, setF] = useState({ rank_end: "", open_price: "", entry_price: "", exit_price: "", close_price: "", entry_amount: "" });
  const [isReal, setIsReal] = useState(false);

  useEffect(() => {
    if (!stock) return;
    setF({
      rank_end: stock.rank_end != null ? String(stock.rank_end) : "",
      open_price: stock.open_price != null ? String(stock.open_price)
        : (stock.criteria?.open_price != null ? String(stock.criteria.open_price) : ""),
      entry_price: stock.entry_price != null ? String(stock.entry_price) : "",
      exit_price: stock.exit_price != null ? String(stock.exit_price) : "",
      close_price: stock.close_price != null ? String(stock.close_price) : "",
      entry_amount: stock.entry_amount != null ? String(stock.entry_amount) : "",
    });
    setIsReal((stock.status || "test").toLowerCase() === "real");
  }, [stock]);

  if (!stock) return null;
  const prevClose = stock.criteria?.prev_close;

  const saveAll = async () => {
    try {
      const body = {};
      if (f.rank_end !== "") body.rank_end = Math.max(0, parseInt(f.rank_end, 10) || 0);
      body.open_price = f.open_price === "" ? null : parseAbbrev(f.open_price);
      body.entry_price = f.entry_price === "" ? null : parseAbbrev(f.entry_price);
      body.exit_price = f.exit_price === "" ? null : parseAbbrev(f.exit_price);
      body.close_price = f.close_price === "" ? null : parseAbbrev(f.close_price);
      body.status = isReal ? "real" : "test";
      if (isReal) {
        body.entry_amount = f.entry_amount === "" ? null : parseAbbrev(f.entry_amount);
      }
      const r = await api.patch(`/stocks/${stock.id}`, body);
      toast.success(`${stock.symbol} — enregistré`);
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    }
  };

  const openP = f.open_price === "" ? null : parseAbbrev(f.open_price);
  const entryP = f.entry_price === "" ? null : parseAbbrev(f.entry_price);
  const exitP = f.exit_price === "" ? null : parseAbbrev(f.exit_price);
  const closeP = f.close_price === "" ? null : parseAbbrev(f.close_price);
  // Preview: for Real → from entry to exit/close. For Test → from open to close.
  const preview = (() => {
    if (isReal) {
      if (entryP == null || entryP <= 0) return null;
      const ref = exitP != null ? exitP : closeP;
      if (ref == null) return null;
      return (ref / entryP - 1) * 100;
    }
    if (openP == null || openP <= 0 || closeP == null) return null;
    return (closeP / openP - 1) * 100;
  })();

  const derivedPct = (price) => {
    if (price == null || prevClose == null || prevClose <= 0) return null;
    return (price / prevClose - 1) * 100;
  };
  const fmtPct = (p) => p == null ? "—" : `${p >= 0 ? "+" : ""}${p.toFixed(2)}%`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="bg-card-1 border-line-strong max-h-[92vh] overflow-y-auto rounded-t-2xl" data-testid="mobile-edit-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display flex items-center gap-2 flex-wrap">
            {stock.symbol}
            <DecisionBadge score={stock.score} size="sm" />
            <StatusBadge status={isReal ? "real" : "test"} />
          </SheetTitle>
          <SheetDescription className="text-muted-1 text-xs">
            {stock.name || " "} · rang #{stock.rank_in_list ?? "—"} · vs clôture veille {prevClose != null ? `${prevClose} $` : "—"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Test / Real switch */}
          <div className="flex items-center gap-3 bg-input border border-line rounded-lg p-3">
            <Switch checked={isReal} onCheckedChange={setIsReal} data-testid="sheet-status-toggle" />
            <div className="text-xs flex-1">
              <div className="font-medium">{isReal ? "Réel — impacte le capital" : "Test — simulation seulement"}</div>
              <div className="text-muted-1 text-[10px] mt-0.5">
                {isReal ? "Prix et montant modifiables" : "Prix d'entrée = prix capturé · montant = allocation proposée"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-1">Rang de fin (0 = hors top)</Label>
              <Input
                value={f.rank_end} onChange={(e) => setF({ ...f, rank_end: e.target.value })}
                className="h-10 mt-1.5 bg-input border-line-strong font-mono-num"
                data-testid="sheet-rankend"
                inputMode="numeric"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-1">% Pré-market (info)</Label>
              <Input
                value={stock.premarket?.percent ?? "—"} disabled
                className="h-10 mt-1.5 bg-input border-line-strong font-mono-num opacity-60"
              />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">
              Prix d'ouverture $ {!isReal && <span className="text-invest">(base gain Test)</span>}
            </Label>
            <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-2 items-center">
              <Input
                value={f.open_price} onChange={(e) => setF({ ...f, open_price: e.target.value })}
                placeholder="ex : 11.20"
                className="h-10 bg-input border-line-strong font-mono-num"
                data-testid="sheet-open-price"
                inputMode="decimal"
              />
              <span className={`text-xs font-mono-num ${derivedPct(openP) == null ? "text-muted-2" : derivedPct(openP) >= 0 ? "text-invest" : "text-ignore"}`}>
                {fmtPct(derivedPct(openP))}
              </span>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">
              Prix d'entrée $ {!isReal && <span className="text-muted-2">(auto Test)</span>}
            </Label>
            <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-2 items-center">
              <Input
                value={f.entry_price} onChange={(e) => setF({ ...f, entry_price: e.target.value })}
                placeholder="ex : 11.78"
                disabled={!isReal}
                className={`h-10 bg-input border-line-strong font-mono-num ${!isReal ? "opacity-60" : ""}`}
                data-testid="sheet-entry-price"
                inputMode="decimal"
              />
              <span className={`text-xs font-mono-num ${derivedPct(entryP) == null ? "text-muted-2" : derivedPct(entryP) >= 0 ? "text-invest" : "text-ignore"}`}>
                {fmtPct(derivedPct(entryP))}
              </span>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">Prix de sortie $ (si vendu avant clôture)</Label>
            <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-2 items-center">
              <Input
                value={f.exit_price} onChange={(e) => setF({ ...f, exit_price: e.target.value })}
                placeholder="laisser vide si non vendu"
                className="h-10 bg-input border-line-strong font-mono-num"
                data-testid="sheet-exit-price"
                inputMode="decimal"
              />
              <span className={`text-xs font-mono-num ${derivedPct(exitP) == null ? "text-muted-2" : derivedPct(exitP) >= 0 ? "text-invest" : "text-ignore"}`}>
                {fmtPct(derivedPct(exitP))}
              </span>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">Prix de clôture $</Label>
            <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-2 items-center">
              <Input
                value={f.close_price} onChange={(e) => setF({ ...f, close_price: e.target.value })}
                placeholder="ex : 15.20"
                className="h-10 bg-input border-line-strong font-mono-num"
                data-testid="sheet-close-price"
                inputMode="decimal"
              />
              <span className={`text-xs font-mono-num ${derivedPct(closeP) == null ? "text-muted-2" : derivedPct(closeP) >= 0 ? "text-invest" : "text-ignore"}`}>
                {fmtPct(derivedPct(closeP))}
              </span>
            </div>
          </div>

          {isReal && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-1">Montant investi $ (laisser vide = allocation auto)</Label>
              <Input
                value={f.entry_amount} onChange={(e) => setF({ ...f, entry_amount: e.target.value })}
                placeholder={stock.effective_allocation != null ? String(stock.effective_allocation) : "auto"}
                className="h-10 mt-1.5 bg-input border-line-strong font-mono-num"
                data-testid="sheet-entry-amount"
                inputMode="decimal"
              />
            </div>
          )}

          {/* Gain preview */}
          <div className="flex items-center justify-between bg-input border border-line rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-1">Gain (sur prix $)</div>
            <div className={`font-mono-num text-xl font-semibold ${preview == null ? "text-muted-2" : preview >= 0 ? "text-invest" : "text-ignore"}`} data-testid="sheet-gain-preview">
              {preview == null ? "en cours" : `${preview >= 0 ? "+" : ""}${preview.toFixed(2)} %`}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button onClick={saveAll} className="w-full h-11 btn-primary" data-testid="sheet-save">
              <Save size={14} className="mr-2" /> Enregistrer
            </Button>
            <Link
              to={`/stock/${stock.id}`}
              className="w-full h-11 border border-line-strong rounded-md flex items-center justify-center gap-2 text-sm hover:bg-card-2 transition-colors"
              data-testid="sheet-open-detail"
            >
              <ExternalLink size={14} /> Ouvrir la fiche complète
            </Link>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// -----------------------------------------------------------------------------
// Day-notes editor
// -----------------------------------------------------------------------------
function DayNotesBlock({ date }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/day-notes/${date}`).then((r) => setNotes(r.data.notes || ""));
  }, [date]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/day-notes/${date}`, { notes });
      toast.success("Commentaire enregistré");
      setOpen(false);
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-input border border-line rounded-lg p-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`day-notes-toggle-${date}`}
      >
        <div className="flex items-center gap-2">
          <StickyNote size={14} className="text-muted-1" />
          <div className="text-[10px] uppercase tracking-widest text-muted-1">Commentaire de journée</div>
        </div>
        <ChevronDown size={14} className={`text-muted-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <div className="mt-3 space-y-2">
          <Textarea
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Contexte du marché, discipline, apprentissages…"
            className="bg-card-1 border-line-strong min-h-[100px]"
            data-testid={`day-notes-textarea-${date}`}
          />
          <Button onClick={save} disabled={saving} className="btn-primary h-9 text-xs" data-testid={`day-notes-save-${date}`}>
            <Save size={12} className="mr-1" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      ) : notes ? (
        <div className="mt-2 text-xs text-muted-1 line-clamp-2">{notes}</div>
      ) : null}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Expandable day row

function SortHeader({ label, k, testId, onSort, arrow }) {
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      className="text-right font-mono-num uppercase tracking-widest text-[9px] text-muted-2 hover:text-app inline-flex items-center gap-1 justify-end w-full"
      data-testid={testId}
    >
      {label} {arrow(k)}
    </button>
  );
}


// -----------------------------------------------------------------------------
// Sortable desktop table for a day (iter16)
// Columns: Rang | Symbole | Investi | %Pré | Prix Entrée $ | Prix Sortie $ | Prix Clôture $ | Gain % | P&L
// Sortable on Investi / Gain / P&L.
// -----------------------------------------------------------------------------
function DesktopTable({ day, date, realOnly, refreshAfter }) {
  const [sortKey, setSortKey] = useState(null);   // "alloc" | "gain" | "pnl"
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key); setSortDir("desc");
    }
  };

  const arrow = (key) => {
    if (sortKey !== key) return <span className="text-muted-2 opacity-40">↕</span>;
    return <span className="text-app">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  const rows = day.stocks
    .filter((s) => !realOnly || (s.status || "test").toLowerCase() === "real")
    .map((s) => {
      const p = day.pnl.detail.find((d) => d.stock_id === s.id);
      return { s, p, gain: composedGain(s) };
    });

  if (sortKey) {
    const sign = sortDir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      const av = sortKey === "score" ? (a.s.score ?? 0)
               : sortKey === "alloc" ? (a.s.effective_allocation ?? 0)
               : sortKey === "gain"  ? (a.gain ?? -Infinity)
               :                       (a.p?.pnl ?? -Infinity);
      const bv = sortKey === "score" ? (b.s.score ?? 0)
               : sortKey === "alloc" ? (b.s.effective_allocation ?? 0)
               : sortKey === "gain"  ? (b.gain ?? -Infinity)
               :                       (b.p?.pnl ?? -Infinity);
      return sign * (av - bv);
    });
  }

  return (
    <div className="bg-card-1 border border-line rounded-lg overflow-x-auto">
      <div className="grid grid-cols-[72px_minmax(140px,1.4fr)_repeat(8,minmax(88px,1fr))] gap-3 px-4 py-2 border-b border-line text-[9px] uppercase tracking-widest text-muted-2">
        <div>Rang</div>
        <div>Symbole</div>
        <SortHeader label="Score" k="score" testId={`sort-score-${date}`} onSort={toggleSort} arrow={arrow} />
        <SortHeader label="Investi" k="alloc" testId={`sort-alloc-${date}`} onSort={toggleSort} arrow={arrow} />
        <div className="text-right">% Pré</div>
        <div className="text-right">Entrée $</div>
        <div className="text-right">Sortie $</div>
        <div className="text-right">Clôture $</div>
        <SortHeader label="Gain %" k="gain" testId={`sort-gain-${date}`} onSort={toggleSort} arrow={arrow} />
        <SortHeader label="P&L $" k="pnl" testId={`sort-pnl-${date}`} onSort={toggleSort} arrow={arrow} />
      </div>
      {rows.map(({ s, p, gain }) => {
        const pre = s.premarket?.percent;
        return (
          <Link
            key={s.id}
            to={`/stock/${s.id}`}
            className="grid grid-cols-[72px_minmax(140px,1.4fr)_repeat(8,minmax(88px,1fr))] gap-3 px-4 py-3 border-b border-line last:border-b-0 hover:bg-card-2 transition-colors items-center"
            data-testid={`history-stock-${date}-${s.symbol}`}
          >
            <div className="text-left">
              <div className="font-mono-num text-sm"><InlineRank stock={s} onChanged={refreshAfter} /></div>
              <div className="text-[10px] text-muted-2 -mt-0.5">
                → <span onClick={(e) => e.stopPropagation()}>
                  <InlineRankEnd stock={s} onUpdated={refreshAfter} />
                </span>
              </div>
            </div>
            <div>
              <div className="font-display font-semibold text-app text-sm">{s.symbol}</div>
              <div className="text-[10px] text-muted-2">{s.name || " "}</div>
              <div className="mt-1"><StatusToggle stock={s} onChanged={refreshAfter} /></div>
            </div>
            <div className="text-right">
              <div className={`font-mono-num text-sm font-semibold ${s.score >= 80 ? "text-invest" : s.score >= 60 ? "text-monitor" : "text-muted-2"}`}>{s.score}</div>
              <div className="text-[10px] text-muted-2">{s.decision_label}</div>
            </div>
            <div className="text-right font-mono-num text-sm">{formatMoney(s.effective_allocation)}</div>
            <div className={`text-right font-mono-num text-xs ${pre == null ? "text-muted-2" : pre >= 0 ? "text-invest" : "text-ignore"}`}>
              {pre == null ? "—" : formatPct(pre)}
            </div>
            <div className="text-right font-mono-num text-sm">
              {s.entry_price != null ? `${Number(s.entry_price).toFixed(4).replace(/\.?0+$/, "")} $` : "—"}
            </div>
            <div className="text-right font-mono-num text-sm">
              {s.exit_price != null ? `${Number(s.exit_price).toFixed(4).replace(/\.?0+$/, "")} $` : "—"}
            </div>
            <div className="text-right font-mono-num text-sm">
              {s.close_price != null ? `${Number(s.close_price).toFixed(4).replace(/\.?0+$/, "")} $` : "—"}
            </div>
            <div className={`text-right font-mono-num text-sm ${gain == null ? "text-muted-2" : gain >= 0 ? "text-invest" : "text-ignore"}`}>
              {gain == null ? "en cours" : `${gain >= 0 ? "+" : ""}${gain.toFixed(2)} %`}
            </div>
            <div className={`text-right font-mono-num text-sm ${p?.pnl == null ? "text-muted-2" : p.pnl >= 0 ? "text-invest" : "text-ignore"}`}>
              {p?.pnl == null ? "—" : formatMoney(p.pnl)}
            </div>
          </Link>
        );
      })}
    </div>
  );
}


// -----------------------------------------------------------------------------
function DayRow({ date, count, capital, reserve, compound, onDeleted, isMobile, realOnly = false }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeStock, setActiveStock] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "desc" });

  const loadDay = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/portfolio/day/${date}`);
      setDay(r.data);
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { if (open && !day) loadDay(); }, [open, day, loadDay]);

  const refreshAfter = () => { loadDay(); };
  const openSheet = (s) => { setActiveStock(s); setSheetOpen(true); };
  const activeStockLive = day && activeStock ? day.stocks.find((x) => x.id === activeStock.id) || activeStock : activeStock;

  const del = async () => {
    try {
      await api.delete(`/days/${date}`);
      toast.success(`Journée ${date} supprimée`);
      onDeleted?.();
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    }
  };

  const dayPnl = day?.pnl?.pnl ?? null;

  return (
    <div className="bg-card-1 border border-line rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 p-3 sm:p-4">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-3 flex-1 text-left min-w-0"
          data-testid={`history-toggle-${date}`}
        >
          {open ? <ChevronDown size={16} className="text-muted-1 shrink-0" /> : <ChevronRight size={16} className="text-muted-1 shrink-0" />}
          <div className="min-w-0">
            <div className="font-display text-sm sm:text-base capitalize truncate">{frDate(date)}</div>
            <div className="text-[10px] sm:text-[11px] font-mono-num text-muted-2 truncate">{date} · {count} titre(s)</div>
          </div>
        </button>
        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-muted-2">Réel</div>
            <div className="font-mono-num text-xs sm:text-sm">{formatMoney(capital)}</div>
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-[9px] uppercase tracking-widest text-muted-2">Réserve</div>
            <div className="font-mono-num text-sm text-invest">{formatMoney(reserve)}</div>
          </div>
          <div className="text-right hidden md:block">
            <div className="text-[9px] uppercase tracking-widest text-muted-2">Composé</div>
            <div className="font-mono-num text-sm">{formatMoney(compound)}</div>
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t border-line p-3 sm:p-4 space-y-4 bg-input">
          {loading ? (
            <div className="text-sm text-muted-1">Chargement…</div>
          ) : day ? (
            <>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="px-3 py-2 bg-card-1 border border-line rounded-lg" data-testid={`history-strategy-${date}`}>
                  <div className="text-[9px] uppercase tracking-widest text-muted-2">Stratégie</div>
                  <div className={`font-display text-sm mt-0.5 ${day.strategy === "filet" ? "text-invest" : "text-app"}`}>
                    {day.strategy === "filet" ? "Filet" : "Sniper"}
                  </div>
                </div>
                <div className="px-3 py-2 bg-card-1 border border-line rounded-lg">
                  <div className="text-[9px] uppercase tracking-widest text-muted-2">Cumul</div>
                  <div className={`font-mono-num text-sm mt-0.5 ${dayPnl == null ? "text-muted-1" : dayPnl >= 0 ? "text-invest" : "text-ignore"}`}>
                    {dayPnl == null ? "—" : formatMoney(dayPnl)}
                  </div>
                </div>
                <Link
                  to={`/day/${date}`}
                  className="w-9 h-9 flex items-center justify-center bg-card-1 border border-line-strong rounded-lg text-app hover:bg-card-2 transition-colors"
                  title="Ouvrir en pleine page"
                  aria-label="Ouvrir en pleine page"
                  data-testid={`history-open-${date}`}
                >
                  <ExternalLink size={14} />
                </Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      className="w-9 h-9 flex items-center justify-center bg-card-1 border border-ignore/30 rounded-lg text-ignore hover:bg-ignore/10 transition-colors"
                      title="Supprimer la journée"
                      aria-label="Supprimer la journée"
                      data-testid={`history-delete-${date}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer la journée {date} ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Toutes les actions et le commentaire de cette journée seront supprimés. Les courbes se recalculent automatiquement.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`history-delete-cancel-${date}`}>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={del} className="bg-ignore text-white hover:bg-ignore/90" data-testid={`history-delete-confirm-${date}`}>
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* ============ MOBILE: compact rows + Sheet ============ */}
              {isMobile ? (
                <div className="bg-card-1 border border-line rounded-lg">
                  <div className="px-3 pt-2.5 pb-2 border-b border-line flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] uppercase tracking-widest text-muted-2 shrink-0">Trier</span>
                    <SortChips
                      testIdPrefix={`history-sort-${date}`}
                      sortKey={sort.key}
                      sortDir={sort.dir}
                      onChange={(k) => setSort((s) => toggleSort(s, k, k === "symbol" ? "asc" : "desc"))}
                      options={[
                        { key: "rank", label: "Rang" },
                        { key: "symbol", label: "Symb." },
                        { key: "score", label: "Score" },
                        { key: "alloc", label: "Alloc." },
                        { key: "gain", label: "Gain %" },
                        { key: "pnl", label: "P&L" },
                      ]}
                    />
                  </div>
                  <div className="grid grid-cols-[32px_1fr_56px_68px_1fr] gap-x-3 px-3 py-2 border-b border-line text-[9px] uppercase tracking-widest text-muted-2">
                    <div>#</div>
                    <div>Symb.</div>
                    <div className="text-right">Score</div>
                    <div className="text-right">Alloc</div>
                    <div className="text-right">Gain / P&L</div>
                  </div>
                  {sortRows(
                    day.stocks.filter((s) => !realOnly || (s.status || "test").toLowerCase() === "real"),
                    sort.key,
                    sort.dir,
                    {
                      rank: (s) => s.rank_in_list ?? Infinity,
                      symbol: (s) => s.symbol,
                      score: (s) => s.score ?? -Infinity,
                      alloc: (s) => day.pnl.detail.find((d) => d.stock_id === s.id)?.allocation ?? s.effective_allocation ?? -Infinity,
                      gain: (s) => composedGain(s),
                      pnl: (s) => day.pnl.detail.find((d) => d.stock_id === s.id)?.pnl ?? null,
                    }
                  ).map((s) => {
                    const p = day.pnl.detail.find((d) => d.stock_id === s.id);
                    const gain = composedGain(s);
                    return (
                      <div
                        key={s.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openSheet(s)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openSheet(s); }}
                        className="w-full grid grid-cols-[32px_1fr_56px_68px_1fr] gap-x-3 px-3 py-3 border-b border-line last:border-b-0 hover:bg-card-2 transition-colors items-center text-left cursor-pointer"
                        data-testid={`history-stock-mobile-${date}-${s.symbol}`}
                      >
                        <div className="font-mono-num text-xs">
                          <InlineRank stock={s} onChanged={refreshAfter} />
                          {s.rank_end != null && (
                            <div className="text-[9px] text-muted-2">→ {s.rank_end === 0 ? "hors" : s.rank_end}</div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-display font-semibold text-sm text-app truncate">{s.symbol}</div>
                          <div className="mt-1"><StatusToggle stock={s} onChanged={refreshAfter} /></div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono-num text-sm font-semibold leading-tight">{s.score}</div>
                          <div className="mt-0.5"><DecisionBadge score={s.score} size="sm" /></div>
                        </div>
                        <div className="font-mono-num text-right text-xs">
                          {p?.allocation == null ? "—" : formatMoney(p.allocation)}
                        </div>
                        <div className={`text-right font-mono-num leading-tight ${gain == null ? "text-muted-2" : gain >= 0 ? "text-invest" : "text-ignore"}`}>
                          <div className="text-sm font-medium">
                            {gain == null ? "—" : `${gain >= 0 ? "+" : ""}${gain.toFixed(1)}%`}
                          </div>
                          <div className={`text-xs mt-0.5 ${p?.pnl == null ? "text-muted-2" : p.pnl >= 0 ? "text-invest" : "text-ignore"}`}>
                            {p?.pnl == null ? "—" : formatMoney(p.pnl)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* ============ DESKTOP: full table with sortable columns + $ prices ============ */
                <DesktopTable
                  day={day}
                  date={date}
                  realOnly={realOnly}
                  refreshAfter={refreshAfter}
                />
              )}

              <div className="text-[10px] text-muted-2 font-mono-num leading-relaxed">
                Sortie et Clôture sont deux mesures distinctes. Le gain compose l'entrée avec la Sortie si elle est renseignée, sinon avec la Clôture. Le pré-market ne compte jamais.
              </div>

              <DayNotesBlock date={date} />

              {/* Mobile edit sheet */}
              <StockEditSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                stock={activeStockLive}
                onSaved={refreshAfter}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// History page
// -----------------------------------------------------------------------------
export default function History() {
  const [days, setDays] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [realOnly, setRealOnly] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([api.get("/history/days"), api.get("/portfolio/summary")]);
      setDays(d.data.slice().reverse());
      setSummary(s.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ptr = usePullToRefresh(load);

  if (loading) return <div className="text-muted-1">Chargement…</div>;

  const byDate = {};
  if (summary) {
    summary.curves.real.forEach((r, i) => {
      byDate[r.date] = {
        real: r.value,
        compound: summary.curves.compound[i]?.value,
        reserve: summary.curves.reserve[i]?.value,
      };
    });
  }

  const cumul = summary ? summary.real_capital + summary.reserve - summary.starting_capital : 0;

  return (
    <div className="space-y-6 relative">
      {/* iter 28.7 — pull-to-refresh indicator */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 z-30 pointer-events-none" style={ptr.indicatorStyle} data-testid="ptr-indicator">
        <div className="mt-2 px-3 py-1.5 rounded-full bg-card-2 border border-line-strong text-[10px] uppercase tracking-widest text-muted-1 flex items-center gap-1.5">
          <RefreshCwIcon size={11} className={ptr.refreshing ? "animate-spin" : ""} style={{ transform: `rotate(${ptr.progress * 360}deg)` }} />
          {ptr.refreshing ? "Rafraîchissement…" : ptr.progress >= 1 ? "Relâche pour rafraîchir" : "Tire pour rafraîchir"}
        </div>
      </div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-2 mb-1">Journal</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold">Journal des jours suivis</h1>
        </div>
        <label className="flex items-center gap-2 text-[11px] text-muted-1 cursor-pointer select-none" data-testid="history-real-only-label">
          <Switch checked={realOnly} onCheckedChange={setRealOnly} className="scale-75 origin-left" data-testid="history-real-only-switch" />
          <span className={realOnly ? "text-invest" : ""}>Réel uniquement</span>
        </label>
      </div>

      {summary && (
        <div className="bg-card-1 border border-line rounded-xl p-4 sm:p-6">
          <div className="text-[10px] uppercase tracking-widest text-muted-2">Cumul réalisé</div>
          <div className={`font-mono-num text-2xl sm:text-3xl font-semibold mt-2 ${cumul >= 0 ? "text-invest" : "text-ignore"}`} data-testid="history-cumul">
            {formatMoney(cumul)}
          </div>
          <div className="text-xs text-muted-1 mt-1">
            {summary.days_traded} jour(s) suivi(s) · Drawdown max {summary.max_drawdown_pct.toFixed(2)}%
          </div>
        </div>
      )}

      {days.length === 0 ? (
        <div className="bg-card-1 border border-line border-dashed rounded-xl p-10 text-center text-muted-1 text-sm">
          Aucun jour enregistré pour le moment.
        </div>
      ) : (
        <div className="space-y-3">
          {days.map((d) => {
            const s = byDate[d.date] || {};
            return (
              <DayRow
                key={d.date}
                date={d.date}
                count={d.count}
                capital={s.real}
                reserve={s.reserve}
                compound={s.compound}
                onDeleted={load}
                isMobile={isMobile}
                realOnly={realOnly}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
